// Settings → Sale menu: the owner names the menu tiles on the Sales screen (Apple, Samsung, Gaming…)
// and puts their own picture on each one (a photo taken on the tablet, or an uploaded one). Saved
// as settings; the Sales screen's menu shows them right away.
import { useState } from 'react';
import { get, patch, post } from '../lib/api';
import { useLoad } from '../lib/hooks';
import { useSession } from '../lib/session';
import { Button, Field, Spinner, useAction, useToast } from '../ui/kit';
import { Icon } from '../ui/icons';
import { photoToDataUrl, pickPhoto } from '../lib/image';

interface Menu { id: string; name: string; default_name: string; cover: string | null; own_cover: boolean; count: number }

export function SaleMenuTab() {
  const { refresh } = useSession();
  const toast = useToast();
  const { run, busy } = useAction();
  const menus = useLoad<{ menus: Menu[] }>(() => get('/api/sale-menu'), []);
  const [names, setNames] = useState<Record<string, string>>({});
  const [covers, setCovers] = useState<Record<string, string | null>>({});
  const [uploading, setUploading] = useState<string | null>(null);
  const list = menus.data?.menus ?? [];
  const nameOf = (m: Menu) => (names[m.id] ?? (m.name === m.default_name ? '' : m.name));
  const coverOf = (m: Menu) => (m.id in covers ? covers[m.id] : m.own_cover ? m.cover : null);
  const changed = list.some((m) => m.id in names || m.id in covers);
  const takePhoto = async (m: Menu, camera: boolean) => {
    const file = await pickPhoto({ camera });
    if (!file) return;
    setUploading(m.id);
    try {
      const dataUrl = await photoToDataUrl(file, 1000, 0.85);
      const res = await post<{ url: string }>('/api/images', { dataUrl });
      setCovers((c) => ({ ...c, [m.id]: res.url }));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That picture could not be saved.', 'error');
    } finally {
      setUploading(null);
    }
  };
  const save = async () => {
    const nameMap: Record<string, string> = {};
    const coverMap: Record<string, string> = {};
    for (const m of list) {
      const n = nameOf(m).trim();
      if (n && n !== m.default_name) nameMap[m.id] = n.slice(0, 30);
      const c = coverOf(m);
      if (c) coverMap[m.id] = c;
    }
    const r = await run(() => patch('/api/settings', { sale_menu_names: Object.keys(nameMap).length ? JSON.stringify(nameMap) : '', sale_menu_covers: Object.keys(coverMap).length ? JSON.stringify(coverMap) : '' }), { success: 'Sale menu saved. The Sales screen shows it now.' });
    if (r) { setNames({}); setCovers({}); menus.reload(); refresh?.(); }
  };
  if (!menus.data) return menus.error ? <p className="error-text">{menus.error.message}</p> : <Spinner />;
  return (
    <section className="card stack">
      <div>
        <h2>Sale menu</h2>
        <p className="small muted" style={{ margin: 0 }}>The tiles on the Sales screen's Menu: call them what you like and put your own picture on each one. Blank keeps the usual name; "Usual picture" puts the built-in one back.</p>
      </div>
      <div className="sm-edit-grid">
        {list.map((m) => {
          const cover = coverOf(m) ?? m.cover;
          return (
            <div key={m.id} className="sm-edit" aria-label={`${m.default_name} tile`}>
              <div className="sm-edit-pic">{cover ? <img src={cover} alt="" /> : <span className="muted">No picture</span>}</div>
              <Field label={`${m.default_name} tile`} hint={`${m.count} item${m.count === 1 ? '' : 's'} on it`}>
                <input className="input" value={nameOf(m)} placeholder={m.default_name} maxLength={30} onChange={(e) => setNames((x) => ({ ...x, [m.id]: e.target.value }))} aria-label={`${m.default_name} tile name`} />
              </Field>
              <div className="row-wrap">
                <Button size="sm" icon="scan" busy={uploading === m.id} onClick={() => takePhoto(m, true)}>Take a picture</Button>
                <Button size="sm" variant="ghost" icon="plus" busy={uploading === m.id} onClick={() => takePhoto(m, false)}>Upload</Button>
                {coverOf(m) ? <Button size="sm" variant="ghost" icon="refresh" onClick={() => setCovers((c) => ({ ...c, [m.id]: null }))}>Usual picture</Button> : null}
              </div>
            </div>
          );
        })}
      </div>
      <div className="row-wrap">
        <Button variant="primary" icon="check" busy={busy} disabled={!changed} onClick={save}>Save the sale menu</Button>
        {changed ? <span className="small muted"><Icon.info /> Not saved yet.</span> : null}
      </div>
    </section>
  );
}
